import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { boards as boardsApi, cards as cardsApi, type Board, type Card, type Activity } from '../lib/api';
import { cn, PHASE_COLORS, timeAgo } from '../lib/utils';
import { ws } from '../lib/websocket';

const KNOWN_AGENTS = new Set(['triager', 'architect', 'reviewer', 'builder', 'tester', 'deployer', 'monitor', 'ci-bot', 'github-bot']);

const PHASE_DESCRIPTIONS: Record<string, string> = {
  triage: 'Analyzing complexity and risk...',
  planning: 'Creating implementation plan...',
  implementation: 'Writing code changes...',
  review: 'Reviewing code quality...',
};

/** A card is "running" only when the runner has an active (non-expired) claim on it */
function isRunnerActive(card: Card): boolean {
  return !!(card.metadata.claim && new Date(card.metadata.claim.expiresAt) > new Date());
}

const ACTIVITY_DOT_COLORS: Record<string, string> = {
  analysis: 'bg-blue-400',
  planning: 'bg-blue-400',
  coding: 'bg-purple-400',
  testing: 'bg-green-400',
  review: 'bg-cyan-400',
  deployment: 'bg-orange-400',
  feedback: 'bg-gray-400',
  error: 'bg-red-400',
  'status-update': 'bg-primary-500',
  question: 'bg-amber-400',
  migration: 'bg-orange-400',
};

interface Props {
  isOpen: boolean;
  onToggle: () => void;
  /** When true, renders just the inner content without the fixed-width shell */
  isMobileOverlay?: boolean;
}

export function ActivitySidebar({ isOpen, onToggle, isMobileOverlay }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Subscribe to WebSocket for real-time sidebar updates
  useEffect(() => {
    const unsub = ws.subscribe((msg) => {
      if (msg.type === 'card-updated' || msg.type === 'card-moved') {
        queryClient.invalidateQueries({ queryKey: ['sidebar-cards'] });
      }
      if (msg.type === 'activity-created') {
        queryClient.invalidateQueries({ queryKey: ['sidebar-cards'] });
        queryClient.invalidateQueries({ queryKey: ['sidebar-activities'] });
      }
    });
    return () => { unsub(); };
  }, [queryClient]);

  // Fetch all boards — fallback poll at 120s for non-current boards
  const { data: boardsData } = useQuery({
    queryKey: ['boards'],
    queryFn: boardsApi.list,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });
  const allBoards: Board[] = boardsData?.data ?? [];

  // Fetch cards for each board — WS-driven, 120s fallback
  const boardIds = allBoards.map((b) => b.id);
  const cardsQueries = useQuery({
    queryKey: ['sidebar-cards', boardIds],
    queryFn: async () => {
      const results = await Promise.all(
        allBoards.map(async (board) => {
          const res = await cardsApi.list(board.id);
          return { boardId: board.id, cards: res.data ?? [] };
        })
      );
      return results;
    },
    enabled: allBoards.length > 0,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });

  const allCardData = cardsQueries.data ?? [];
  const boardMap = new Map(allBoards.map((b) => [b.id, b]));

  // Derive running and input-required cards
  const runningCards: (Card & { boardName: string })[] = [];
  const inputRequiredCards: (Card & { boardName: string })[] = [];

  for (const { boardId, cards } of allCardData) {
    const board = boardMap.get(boardId);
    if (!board) continue;
    for (const card of cards) {
      if (isRunnerActive(card) && !card.metadata.inputRequired) {
        runningCards.push({ ...card, boardName: board.name });
      }
      if (card.metadata.inputRequired) {
        inputRequiredCards.push({ ...card, boardName: board.name });
      }
    }
  }

  // Fetch activities for recent cards (sorted by updatedAt, take top 10)
  const recentCards = allCardData
    .flatMap(({ boardId, cards }) =>
      cards.map((c) => ({ ...c, boardId }))
    )
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 10);

  const recentCardIds = recentCards.map((c) => c.id).sort();
  const activitiesQuery = useQuery({
    queryKey: ['sidebar-activities', recentCardIds],
    queryFn: async () => {
      const results = await Promise.all(
        recentCards.map(async (card) => {
          try {
            const res = await cardsApi.activities(card.boardId, card.id);
            return (res.data ?? []).map((a) => ({
              ...a,
              _cardTitle: card.title,
              _boardId: card.boardId,
            }));
          } catch {
            return [];
          }
        })
      );
      return results.flat();
    },
    enabled: recentCards.length > 0,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });

  const recentActivities = (activitiesQuery.data ?? [])
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 30);

  const navigateToCard = (boardId: string, cardId: string) => {
    navigate(`/boards/${boardId}?card=${cardId}`);
  };

  // Collapsed state (desktop only)
  if (!isOpen && !isMobileOverlay) {
    return (
      <div className="flex w-10 flex-col items-center border-l border-gray-800 bg-surface-850 py-4">
        <button
          onClick={onToggle}
          className="rounded-md p-1.5 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
          title="Open activity sidebar"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        {runningCards.length > 0 && (
          <div className="mt-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500/20 text-[10px] font-bold text-primary-400">
            {runningCards.length}
          </div>
        )}
        {inputRequiredCards.length > 0 && (
          <div className="mt-2 flex h-5 w-5 items-center justify-center rounded-full bg-amber-500/20 text-[10px] font-bold text-amber-400">
            {inputRequiredCards.length}
          </div>
        )}
      </div>
    );
  }

  // Mobile overlay: render just the content, no outer shell
  if (isMobileOverlay) {
    return <ActivityContent
      runningCards={runningCards}
      inputRequiredCards={inputRequiredCards}
      recentActivities={recentActivities}
      navigateToCard={navigateToCard}
    />;
  }

  return (
    <div className="flex w-80 flex-col border-l border-gray-800 bg-surface-850">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Activity</span>
        <button
          onClick={onToggle}
          className="rounded-md p-1 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
          title="Collapse sidebar"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <ActivityContent
          runningCards={runningCards}
          inputRequiredCards={inputRequiredCards}
          recentActivities={recentActivities}
          navigateToCard={navigateToCard}
        />
      </div>
    </div>
  );
}

/** Shared inner content used by both desktop sidebar and mobile overlay */
function ActivityContent({
  runningCards,
  inputRequiredCards,
  recentActivities,
  navigateToCard,
}: {
  runningCards: (Card & { boardName: string })[];
  inputRequiredCards: (Card & { boardName: string })[];
  recentActivities: (Activity & { _cardTitle?: string; _boardId?: string })[];
  navigateToCard: (boardId: string, cardId: string) => void;
}) {
  return (
    <>
      {/* Running */}
      {runningCards.length > 0 && (
        <div className="border-b border-gray-800 p-4">
          <div className="mb-3 flex items-center gap-2">
            <div className="h-2 w-2 animate-pulse rounded-full bg-green-400" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              Running ({runningCards.length})
            </span>
          </div>
          <div className="space-y-2">
            {runningCards.map((card) => {
              const claimPhase = card.metadata.claim?.phase ?? card.status;
              return (
                <button
                  key={card.id}
                  onClick={() => navigateToCard(card.boardId, card.id)}
                  className="w-full rounded-lg border border-gray-800 bg-surface-900 p-2.5 text-left transition-colors hover:border-gray-700"
                >
                  <p className="truncate text-xs font-medium text-gray-200">{card.title}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className={cn('rounded-md px-1.5 py-0.5 text-[10px] font-medium', PHASE_COLORS[claimPhase] || 'bg-gray-800 text-gray-400')}>
                      {claimPhase}
                    </span>
                    <span className="truncate text-[10px] text-gray-600">{card.boardName}</span>
                  </div>
                  <p className="mt-1 text-[10px] italic text-gray-600">
                    {PHASE_DESCRIPTIONS[claimPhase] || 'Processing...'}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Needs Input */}
      {inputRequiredCards.length > 0 && (
        <div className="border-b border-gray-800 p-4">
          <div className="mb-3 flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-amber-400" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-400">
              Needs Input ({inputRequiredCards.length})
            </span>
          </div>
          <div className="space-y-2">
            {inputRequiredCards.map((card) => {
              const openCount = card.metadata.questions?.filter((q) => q.status === 'open').length ?? 0;
              return (
                <button
                  key={card.id}
                  onClick={() => navigateToCard(card.boardId, card.id)}
                  className="w-full rounded-lg border border-amber-500/20 bg-amber-500/5 p-2.5 text-left transition-colors hover:border-amber-500/30"
                >
                  <p className="truncate text-xs font-medium text-gray-200">{card.title}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-400">
                      {openCount} question{openCount !== 1 ? 's' : ''}
                    </span>
                    <span className="truncate text-[10px] text-gray-600">{card.boardName}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* History */}
      <div className="p-4">
        <span className="mb-3 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
          Recent Activity
        </span>
        {recentActivities.length === 0 ? (
          <p className="text-xs text-gray-600">No recent activity</p>
        ) : (
          <div className="space-y-2.5">
            {recentActivities.map((activity) => {
              const actor = (activity.metadata as any)?.actor || activity.agentId;
              const isAgent = KNOWN_AGENTS.has(actor);
              const fromCol = (activity.metadata as any)?.fromColumnName;
              const toCol = (activity.metadata as any)?.toColumnName;

              return (
                <button
                  key={activity.id}
                  onClick={() => navigateToCard((activity as any)._boardId, activity.cardId)}
                  className="flex w-full gap-2.5 text-left"
                >
                  <div className={cn('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', ACTIVITY_DOT_COLORS[activity.type] || 'bg-gray-500')} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-gray-300">{activity.title}</p>
                    {fromCol && toCol ? (
                      <p className="truncate text-[10px] text-gray-500">
                        {fromCol} → {toCol}
                      </p>
                    ) : activity.description ? (
                      <p className="truncate text-[10px] text-gray-500">{activity.description}</p>
                    ) : null}
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <span className="truncate text-[10px] text-gray-600">
                        {(activity as any)._cardTitle}
                      </span>
                      <span className="text-[10px] text-gray-700">·</span>
                      {isAgent ? (
                        <span className="shrink-0 text-[10px] text-purple-400">{actor}</span>
                      ) : (
                        <span className="shrink-0 truncate text-[10px] text-gray-600">{actor}</span>
                      )}
                      <span className="text-[10px] text-gray-700">·</span>
                      <span className="shrink-0 text-[10px] text-gray-700">{timeAgo(activity.createdAt)}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
